/**
 * Holds the opaque boot overlay up until the page is genuinely ready to be
 * looked at, then takes it down for good.
 *
 * The overlay itself is markup + CSS (see src/styles/transitions.css) put on
 * screen by an inline <head> script, so it is already painted before this
 * module - or three.js, or the fonts - has been fetched at all. All this
 * does is decide when to let go.
 */

/** Below this the gate would read as a flicker rather than a hold, so it is
 *  held to it. Only ever reached on a warm cache. */
const MIN_VISIBLE_MS = 450;

/** Nothing gets to hold the site hostage. If the scene is still not ready by
 *  here, the visitor gets the page with whatever backdrop is up. */
const MAX_WAIT_MS = 7000;

/** Matches the .boot opacity transition in transitions.css, plus slack. */
const FADE_OUT_MS = 700;

export function runBootGate(ready: Promise<unknown>): void {
  const root = document.documentElement;
  const screen = document.querySelector<HTMLElement>("[data-boot]");

  // No overlay, or the inline head script never ran (so nothing is covering
  // anything). Either way there is no gate to lift.
  if (!screen || !root.classList.contains("is-booting")) {
    root.classList.remove("is-booting");
    screen?.remove();
    return;
  }

  const openedAt = performance.now();
  let lifted = false;

  const lift = (): void => {
    if (lifted) return;
    lifted = true;

    const held = performance.now() - openedAt;
    window.setTimeout(() => {
      root.classList.remove("is-booting");
      // Removed rather than left in place at opacity 0: it is a full-viewport
      // element that would otherwise stay in the layer tree for the life of
      // the session, and the SPA never has a second first load to reuse it for.
      window.setTimeout(() => screen.remove(), FADE_OUT_MS);
    }, Math.max(0, MIN_VISIBLE_MS - held));
  };

  const timeout = new Promise<void>((resolve) => window.setTimeout(resolve, MAX_WAIT_MS));
  // `catch` before the race, not after: a scene that fails to boot must still
  // lift the gate, and an unhandled rejection here would leave it up.
  void Promise.race([ready.then(() => undefined, () => undefined), timeout]).then(lift);
}

/**
 * Resolves once the webfonts have settled, so the boot gate does not lift
 * onto a page that immediately reflows from the fallback serif into
 * Garamond. Capped, because a font that never arrives must not be able to
 * hold the gate on its own.
 */
export function fontsSettled(maxWaitMs = 2500): Promise<unknown> {
  if (!document.fonts) return Promise.resolve();
  return Promise.race([
    document.fonts.ready,
    new Promise<void>((resolve) => window.setTimeout(resolve, maxWaitMs)),
  ]);
}
